import { Board, Cell, COLS, CONNECT, Coord, Disc, ROWS } from './types';

/** Creates an empty ROWS x COLS board. */
export function createBoard(): Board {
  return Array.from({ length: ROWS }, () =>
    Array.from({ length: COLS }, () => 0 as Cell),
  );
}

/** Deep-clones a board so callers can mutate without affecting the original. */
export function cloneBoard(board: Board): Board {
  return board.map((row) => row.slice());
}

export function isValidColumn(col: number): boolean {
  return Number.isInteger(col) && col >= 0 && col < COLS;
}

/**
 * The row index where a disc dropped into `col` would land,
 * or null if the column is full or invalid.
 */
export function getDropRow(board: Board, col: number): number | null {
  if (!isValidColumn(col)) return null;
  for (let row = ROWS - 1; row >= 0; row--) {
    if (board[row][col] === 0) return row;
  }
  return null;
}

export function isColumnFull(board: Board, col: number): boolean {
  return getDropRow(board, col) === null;
}

/** The list of columns that can still accept a disc. */
export function getValidColumns(board: Board): number[] {
  const cols: number[] = [];
  for (let col = 0; col < COLS; col++) {
    if (!isColumnFull(board, col)) cols.push(col);
  }
  return cols;
}

export function isBoardFull(board: Board): boolean {
  return getValidColumns(board).length === 0;
}

/**
 * Returns a NEW board with `disc` dropped into `col`, plus the landing row.
 * Returns null if the move is illegal (invalid index or full column).
 * The input board is never mutated.
 */
export function dropDisc(
  board: Board,
  col: number,
  disc: Disc,
): { board: Board; row: number } | null {
  const row = getDropRow(board, col);
  if (row === null) return null;
  const next = cloneBoard(board);
  next[row][col] = disc;
  return { board: next, row };
}

/** The four directions to scan for a line: horizontal, vertical, and both diagonals. */
const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 1], // →  horizontal
  [1, 0], // ↓  vertical
  [1, 1], // ↘  diagonal down-right
  [1, -1], // ↙ diagonal down-left
];

/**
 * If the disc at (row, col) belongs to a line of at least CONNECT same-coloured
 * discs, returns the coordinates of that line (ordered); otherwise null.
 */
export function winningLineAt(board: Board, row: number, col: number): Coord[] | null {
  const disc = board[row][col];
  if (disc === 0) return null;

  for (const [dr, dc] of DIRECTIONS) {
    const line: Coord[] = [{ row, col }];

    // Extend forwards along the direction.
    for (let step = 1; step < CONNECT; step++) {
      const r = row + dr * step;
      const c = col + dc * step;
      if (r < 0 || r >= ROWS || c < 0 || c >= COLS || board[r][c] !== disc) break;
      line.push({ row: r, col: c });
    }
    // Extend backwards along the direction.
    for (let step = 1; step < CONNECT; step++) {
      const r = row - dr * step;
      const c = col - dc * step;
      if (r < 0 || r >= ROWS || c < 0 || c >= COLS || board[r][c] !== disc) break;
      line.unshift({ row: r, col: c });
    }

    if (line.length >= CONNECT) return line;
  }
  return null;
}

/**
 * Scans the entire board for any winning line.
 * Returns the winning disc and its line, or null if there is no winner.
 * Prefer `winningLineAt` after a known move; this is for full-board checks.
 */
export function findWinner(board: Board): { disc: Disc; line: Coord[] } | null {
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      if (board[row][col] === 0) continue;
      const line = winningLineAt(board, row, col);
      if (line) return { disc: board[row][col] as Disc, line };
    }
  }
  return null;
}
